import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MessageCircle, Send, CheckCircle, ArrowRight, ArrowLeft, AlertCircle, ChevronDown, Search, X } from 'lucide-react';
import ProjectScopeFieldQuestion from './ProjectScopeFieldQuestion';
import { resolveNextVisibleRef, firstVisibleRef, isAnswered, buildReadableAnswers } from '../utils/projectScopeForm';

const shouldSkipContactInfo = (field) => Boolean(field?.contactInfoKey);

// ── All 11 services + common extras ────────────────────────────────────────
const PROJECT_TYPES = [
  { group: 'Web & Apps',         label: 'Web Development'              },
  { group: 'Web & Apps',         label: 'Mobile App Development'       },
  { group: 'Web & Apps',         label: 'E-commerce Platform'          },
  { group: 'Web & Apps',         label: 'Web Application / Portal'     },
  { group: 'Web & Apps',         label: 'Performance Optimisation'     },
  { group: 'Web & Apps',         label: 'API / Backend Development'    },
  { group: 'Infrastructure',     label: 'Smart Architecture'           },
  { group: 'Infrastructure',     label: 'Cloud & DevOps'               },
  { group: 'Infrastructure',     label: 'Networking & Infrastructure'  },
  { group: 'Security & Policy',  label: 'Fortress Security'            },
  { group: 'Security & Policy',  label: 'Security Audit'               },
  { group: 'Security & Policy',  label: 'Digital Policy & Compliance'  },
  { group: 'Growth',             label: 'Automation & Integrations'    },
  { group: 'Growth',             label: 'Digital Marketing & SEO'      },
  { group: 'Consulting',         label: 'AV System Consulting'         },
  { group: 'Consulting',         label: 'IT Support & Maintenance'     },
  { group: 'Other',              label: 'Other / Not sure yet'         },
];

// ── Searchable combobox ─────────────────────────────────────────────────────
const SearchableSelect = ({ value, onChange, placeholder = 'Search or select…', required }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const wrapRef = useRef(null);

  const filtered = query.trim()
    ? PROJECT_TYPES.filter(t => t.label.toLowerCase().includes(query.toLowerCase()))
    : PROJECT_TYPES;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset highlight when filter changes
  useEffect(() => { setHighlighted(0); }, [query]);

  const select = (label) => {
    onChange(label);
    setQuery('');
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open) { if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { setOpen(true); e.preventDefault(); } return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlighted]) select(filtered[highlighted].label); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlighted}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  // Group headers for ungrouped display when no query
  const groups = [...new Set(filtered.map(t => t.group))];

  return (
    <div ref={wrapRef} className="relative">
      {/* Hidden native input for form validation */}
      <input type="text" required={required} value={value} readOnly tabIndex={-1}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />

      {/* Trigger */}
      <div
        className={`flex items-center w-full border px-4 py-3 cursor-pointer transition-colors duration-200 ${
          open ? 'border-orange-500 bg-gray-900' : 'border-gray-700 bg-gray-900 hover:border-gray-500'
        }`}
        style={{ borderRadius: '2px' }}
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={open}
      >
        {open ? (
          <Search className="w-4 h-4 text-gray-500 shrink-0 mr-3" />
        ) : (
          <ChevronDown className={`w-4 h-4 text-gray-500 shrink-0 mr-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}

        {open ? (
          <input
            ref={inputRef}
            className="flex-1 bg-transparent text-white text-sm outline-none placeholder-gray-500"
            placeholder="Type to search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span className={`flex-1 text-sm ${value ? 'text-white' : 'text-gray-500'}`}>
            {value || placeholder}
          </span>
        )}

        {value && !open && (
          <button onClick={clear} className="ml-2 text-gray-600 hover:text-orange-400 transition-colors shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="absolute z-50 w-full mt-1 bg-gray-900 border border-gray-700 overflow-hidden"
            style={{ borderRadius: '2px', maxHeight: '260px', overflowY: 'auto' }}
            ref={listRef}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
          >
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-600">No match — try "web" or "security"</div>
            )}

            {query.trim() ? (
              // Flat list when searching
              filtered.map((item, idx) => (
                <div
                  key={item.label}
                  data-idx={idx}
                  className={`px-4 py-2.5 cursor-pointer text-sm transition-colors duration-100 ${
                    idx === highlighted
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  onMouseEnter={() => setHighlighted(idx)}
                  onMouseDown={(e) => { e.preventDefault(); select(item.label); }}
                >
                  {item.label}
                </div>
              ))
            ) : (
              // Grouped when browsing
              groups.map(group => {
                const items = filtered.filter(t => t.group === group);
                const baseIdx = filtered.findIndex(t => t.group === group);
                return (
                  <div key={group}>
                    <div className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-orange-500/60 border-b border-gray-800 bg-gray-950/60">
                      {group}
                    </div>
                    {items.map((item, i) => {
                      const idx = baseIdx + i;
                      return (
                        <div
                          key={item.label}
                          data-idx={idx}
                          className={`px-4 py-2.5 cursor-pointer text-sm transition-colors duration-100 ${
                            idx === highlighted
                              ? 'bg-orange-500 text-white'
                              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                          }`}
                          onMouseEnter={() => setHighlighted(idx)}
                          onMouseDown={(e) => { e.preventDefault(); select(item.label); }}
                        >
                          {item.label}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Main contact section ────────────────────────────────────────────────────
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: ''
  });
  const [step, setStep] = useState('basic');
  const [schema, setSchema] = useState(null);
  const [schemaLoadError, setSchemaLoadError] = useState('');
  const [currentRef, setCurrentRef] = useState(null);
  const [history, setHistory] = useState([]);
  const [valuesByRef, setValuesByRef] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const scopeStepRef = useRef(null);

  useEffect(() => {
    fetch('/api/founding-form/schema')
      .then((res) => { if (!res.ok) throw new Error('failed'); return res.json(); })
      .then(setSchema)
      .catch(() => setSchemaLoadError('Could not load the project-scope questions. You can still send your message below.'));
  }, []);

  useEffect(() => {
    if (step === 'scope' && schema && currentRef === null) {
      setCurrentRef(firstVisibleRef(schema, shouldSkipContactInfo));
    }
  }, [step, schema, currentRef]);

  useEffect(() => {
    if (step !== 'scope' || !currentRef) return;
    scopeStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step, currentRef]);

  useEffect(() => {
    if (!isSubmitted) return;
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isSubmitted]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBasicSubmit = (e) => {
    e.preventDefault();
    if (!schema || schemaLoadError) { submitContact({}); return; }
    setStep('scope');
  };

  const currentField = schema?.fields.find((f) => f.ref === currentRef);
  const currentValue = currentRef ? valuesByRef[currentRef] : undefined;

  const setScopeValue = useCallback((ref, value) => {
    setValuesByRef((prev) => ({ ...prev, [ref]: value }));
  }, []);

  const goNextScope = () => {
    if (!schema || !currentField) return;
    if (!isAnswered(currentField, currentValue)) return;
    const nextRef = resolveNextVisibleRef(currentRef, valuesByRef, schema, shouldSkipContactInfo);
    setHistory((prev) => [...prev, currentRef]);
    if (!nextRef) { submitContact(buildReadableAnswers(schema, valuesByRef)); return; }
    setCurrentRef(nextRef);
  };

  const goBackScope = () => {
    if (history.length === 0) { setStep('basic'); return; }
    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentRef(previous);
  };

  const submitContact = async (answers) => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, answers }),
      });
      const data = await response.json();
      if (response.ok) { setIsSubmitted(true); }
      else { setSubmitError(data.error || 'Failed to submit contact form'); }
    } catch (error) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    setIsSubmitted(false);
    setStep('basic');
    setFormData({ name: '', email: '', company: '', projectType: '', message: '' });
    setHistory([]);
    setValuesByRef({});
    setCurrentRef(null);
  };

  const currentIndex = schema && currentRef ? schema.fields.findIndex((f) => f.ref === currentRef) : 0;
  const progress = schema && schema.fields.length > 1 ? Math.round((currentIndex / (schema.fields.length - 1)) * 100) : 0;
  const isLastField = schema && currentField ? resolveNextVisibleRef(currentRef, valuesByRef, schema, shouldSkipContactInfo) === null : false;

  if (isSubmitted) {
    return (
      <section id="contact" className="py-28 relative bg-gray-950">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-lg mx-auto text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="w-16 h-16 bg-orange-500 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '2px' }}>
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-black text-white mb-4 uppercase tracking-tight">Message Received.</h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              We'll get back to you within 24 hours to talk through your project — no sales pitch, just honest conversation.
            </p>
            <button
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 font-black text-sm uppercase tracking-wider transition-colors"
              style={{ borderRadius: '2px' }}
              onClick={resetAll}
            >
              Send Another Message
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-28 relative bg-gray-950">
      <div className="container mx-auto px-6">

        {/* Header */}
        <div className="mb-16">
          <div className="text-xs text-orange-500 font-black uppercase tracking-widest mb-4">Get in Touch</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-6xl font-black leading-none tracking-tight">
              <span className="text-white">Let's build</span>
              <br />
              <span className="text-orange-500">something real.</span>
            </h2>
            <p className="text-gray-400 text-base max-w-sm md:text-right leading-relaxed">
              No sales pitch. No buzzwords. Just an honest conversation about what you need.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">

          {/* Left — contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="space-y-6 mb-12">
              {[
                { icon: Mail, label: 'Email', value: 'hello@tuneuptech.co.ke', sub: 'Best for detailed project discussions', color: 'text-orange-400' },
                { icon: Phone, label: 'Phone', value: '+254 754 709 099', sub: 'Mon–Fri, 9AM–6PM EAT', color: 'text-orange-400' },
                { icon: MessageCircle, label: 'WhatsApp', value: '+254 754 709 099', sub: 'Fastest response', color: 'text-orange-400', href: 'https://wa.me/254754709099' },
              ].map(({ icon: Icon, label, value, sub, color, href }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="w-11 h-11 border border-gray-800 flex items-center justify-center shrink-0" style={{ borderRadius: '2px' }}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <div>
                    <div className="text-xs font-black uppercase tracking-widest text-gray-600 mb-0.5">{label}</div>
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-white font-semibold hover:text-orange-400 transition-colors">{value}</a>
                    ) : (
                      <div className="text-white font-semibold">{value}</div>
                    )}
                    <div className="text-xs text-gray-600 mt-0.5">{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-orange-900/40 bg-orange-950/20 p-6" style={{ borderRadius: '2px', borderLeft: '3px solid #f97316' }}>
              <div className="text-sm font-black text-white uppercase tracking-wide mb-4">Why TuneupTech?</div>
              <ul className="space-y-2.5">
                {[
                  '5+ years building for Kenyan businesses',
                  'AI scoping before a single invoice',
                  'DPA-compliant by default',
                  'We tell you if it\'s a 2-week or 2-month job',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {step === 'basic' && (
              <form onSubmit={handleBasicSubmit} className="space-y-5">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Name *</label>
                    <input
                      type="text" name="name" value={formData.name} onChange={handleChange} required
                      className="w-full bg-gray-900 border border-gray-700 focus:border-orange-500 px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
                      style={{ borderRadius: '2px' }}
                      placeholder="David Mutua"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Email *</label>
                    <input
                      type="email" name="email" value={formData.email} onChange={handleChange} required
                      className="w-full bg-gray-900 border border-gray-700 focus:border-orange-500 px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
                      style={{ borderRadius: '2px' }}
                      placeholder="david@company.co.ke"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Company</label>
                  <input
                    type="text" name="company" value={formData.company} onChange={handleChange}
                    className="w-full bg-gray-900 border border-gray-700 focus:border-orange-500 px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
                    style={{ borderRadius: '2px' }}
                    placeholder="Kenya Enterprises Ltd"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Project Type *</label>
                  <SearchableSelect
                    required
                    value={formData.projectType}
                    onChange={(label) => setFormData({ ...formData, projectType: label })}
                    placeholder="Search or select a service…"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Project Details *</label>
                  <textarea
                    name="message" value={formData.message} onChange={handleChange} required rows={6}
                    className="w-full bg-gray-900 border border-gray-700 focus:border-orange-500 px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors resize-none"
                    style={{ borderRadius: '2px' }}
                    placeholder="Tell us about your business, target audience, and what you'd like to achieve…"
                  />
                </div>

                {schemaLoadError && (
                  <div className="border border-orange-900/40 bg-orange-950/20 p-3 flex items-center gap-2" style={{ borderRadius: '2px' }}>
                    <AlertCircle className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-orange-300 text-sm">{schemaLoadError}</span>
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-4 font-black text-sm uppercase tracking-wider flex items-center justify-center gap-3 transition-colors"
                  style={{ borderRadius: '2px' }}
                  whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending…</>
                  ) : schemaLoadError ? (
                    <><Send className="w-4 h-4" /> Send Message</>
                  ) : (
                    <>Continue: Tell Us About the Project <ArrowRight className="w-4 h-4" /></>
                  )}
                </motion.button>
              </form>
            )}

            {step === 'scope' && (
              <div ref={scopeStepRef} style={{ scrollMarginTop: '100px' }}>
                {!schema && (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-gray-700 border-t-orange-400 rounded-full animate-spin mx-auto" />
                  </div>
                )}
                {schema && currentField && (
                  <>
                    <div className="h-px bg-gray-800 mb-1 overflow-hidden">
                      <div className="h-full bg-orange-500 transition-all duration-300" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-[10px] text-gray-600 uppercase tracking-widest font-mono mb-8">{progress}% complete</div>

                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentRef}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ProjectScopeFieldQuestion
                          field={currentField}
                          value={currentValue}
                          onChange={(v) => setScopeValue(currentField.ref, v)}
                          onSubmitOnEnter={goNextScope}
                        />
                      </motion.div>
                    </AnimatePresence>

                    {submitError && (
                      <div className="border border-red-900/40 bg-red-950/20 p-3 flex items-center gap-2 mt-4" style={{ borderRadius: '2px' }}>
                        <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                        <span className="text-red-400 text-sm">{submitError}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-8">
                      <button onClick={goBackScope} className="text-gray-600 hover:text-white text-sm flex items-center gap-1.5 transition-colors font-medium">
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>
                      <motion.button
                        onClick={goNextScope}
                        disabled={!isAnswered(currentField, currentValue) || isSubmitting}
                        className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-7 py-3 font-black text-sm uppercase tracking-wider flex items-center gap-2 transition-colors"
                        style={{ borderRadius: '2px' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isSubmitting ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>{isLastField ? 'Send Message' : 'Continue'} <ArrowRight className="w-4 h-4" /></>
                        )}
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
